import { useEffect, useState, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { Navigate, Outlet, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { authApi, getAccessToken, clearTokens, businessApi, billingApi, membershipApi, type User, type Business, type FeatureFlags, type BillingStatus, type MembershipRole } from '@/lib/api';
import { configureCurrency } from '@/lib/currency';
import { AppSidebar } from './AppSidebar';
import { SidebarProvider, SidebarTrigger, SidebarInset } from '@/components/ui/sidebar';
import { HeaderAlerts } from './HeaderAlerts';
import { NotificationBell } from './NotificationBell';
import { UpgradeModal } from './Modals/UpgradeModal';
import { TrialExpiredModal } from './Modals/TrialExpiredModal';
import { PaymentFailedModal } from './Modals/PaymentFailedModal';
import { SubscriptionCancelledModal } from './Modals/SubscriptionCancelledModal';
import { LegacyExpiredModal } from './Modals/LegacyExpiredModal';
import { GlobalEmailVerificationModal } from './Modals/GlobalEmailVerificationModal';
import { GlobalViewOnlyBlockModal } from './Modals/GlobalViewOnlyBlockModal';
import { useUpgradeModal } from '@/contexts/UpgradeModalContext';
import { createContext, useContext } from 'react';

interface AppContextValue {
  user: User | null;
  currentBusiness: Business | null;
  featureFlags: FeatureFlags | null;
  billingStatus: BillingStatus | null;
  userRole: MembershipRole | null;
  isViewOnly: boolean;
  refreshFeatureFlags: () => void;
  refreshUser: () => Promise<void>;
}

const AppContext = createContext<AppContextValue | undefined>(undefined);

export function useApp(): AppContextValue {
  const context = useContext(AppContext);
  if (!context) {
    // Return safe defaults instead of throwing - components should handle null values
    // This prevents crashes during React concurrent rendering or HMR
    return {
      user: null,
      currentBusiness: null,
      featureFlags: null,
      billingStatus: null,
      userRole: null,
      isViewOnly: false,
      refreshFeatureFlags: () => {},
      refreshUser: async () => {},
    };
  }
  return {
    ...context,
    isViewOnly: context.userRole === 'view_only',
  };
}

function EmailVerifiedSuccessModal({ onClose }: { onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        onClick={onClose}
        className="absolute inset-0"
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
        }}
      />
      <div className="relative w-full max-w-md">
        <div className="relative bg-white dark:bg-zinc-900 rounded-[32px] shadow-2xl overflow-hidden">
          <div className="px-8 py-10 md:px-10">
            <div className="text-center py-4">
              <div className="relative mx-auto w-16 h-16 mb-6">
                <div className="absolute inset-0 rounded-full bg-emerald-500/20 dark:bg-emerald-400/20 animate-ping" style={{ animationDuration: '1.5s' }} />
                <div className="relative w-full h-full rounded-full bg-emerald-500 dark:bg-emerald-400 flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-white"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path
                      d="M5 13l4 4L19 7"
                      className="animate-[draw_0.3s_ease-out_0.2s_forwards]"
                      style={{
                        strokeDasharray: 24,
                        strokeDashoffset: 24,
                      }}
                    />
                  </svg>
                </div>
              </div>
              <h2 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100 mb-2">
                Email verified!
              </h2>
              <p className="text-zinc-500 dark:text-zinc-400">
                You're all set to send invoices and quotes.
              </p>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes draw {
          to { stroke-dashoffset: 0; }
        }
      `}</style>
    </div>
  );
}

export function AppLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { businessId } = useParams<{ businessId: string }>();
  const [isChecking, setIsChecking] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags | null>(null);
  const [billingStatus, setBillingStatus] = useState<BillingStatus | null>(null);
  const [userRole, setUserRole] = useState<MembershipRole | null>(null);
  const {
    isTrialExpiredOpen, showTrialExpiredModal, closeTrialExpiredModal,
    isPaymentFailedOpen, showPaymentFailedModal, closePaymentFailedModal,
    isSubscriptionCancelledOpen, showSubscriptionCancelledModal, closeSubscriptionCancelledModal,
    isLegacyExpiredOpen, showLegacyExpiredModal, closeLegacyExpiredModal,
  } = useUpgradeModal();
  const trialExpiredShown = useRef(false);
  const paymentFailedShown = useRef(false);
  const subscriptionCancelledShown = useRef(false);
  const legacyExpiredShown = useRef(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showEmailVerifiedModal, setShowEmailVerifiedModal] = useState(false);

  // Detect ?email_verified=1 from one-click verification link
  useEffect(() => {
    if (searchParams.get('email_verified') === '1') {
      setShowEmailVerifiedModal(true);
      // Refresh user so is_email_verified updates and GlobalEmailVerificationModal closes
      authApi.getMe().then((response) => {
        if (response.data) setUser(response.data);
      });
      // Clean up the URL
      searchParams.delete('email_verified');
      setSearchParams(searchParams, { replace: true });
      // Auto-dismiss after 3 seconds
      setTimeout(() => setShowEmailVerifiedModal(false), 3000);
    }
  }, [searchParams, setSearchParams]);

  // Listen for auth changes in other tabs/windows
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'accessToken') {
        if (e.newValue === null) {
          // Token was removed (logout) - redirect to login
          navigate('/login');
        } else if (e.oldValue !== null && e.newValue !== e.oldValue) {
          // Token changed (different account) - reload to get new user data
          window.location.reload();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [navigate]);

  useEffect(() => {
    const token = getAccessToken();

    if (!token) {
      setIsChecking(false);
      return;
    }

    authApi.getMe().then(async (response) => {
      if (response.error) {
        clearTokens();
        setIsChecking(false);
        return;
      }

      if (response.data) {
        setUser(response.data);
        Sentry.setUser({ email: response.data.email });
      }
      setIsChecking(false);
    });
  }, []);

  const currentBusiness = user?.businesses.find((b) => b.id === businessId) || null;

  useEffect(() => {
    if (currentBusiness?.default_currency) {
      configureCurrency(currentBusiness.default_currency, currentBusiness.currency_code);
    }
  }, [currentBusiness?.default_currency, currentBusiness?.currency_code]);

  useEffect(() => {
    if (!currentBusiness?.id || isChecking) return;
    businessApi.getFeatureFlags(currentBusiness.id).then((response) => {
      if (response.data) {
        setFeatureFlags(response.data);
      }
    });
  }, [currentBusiness?.id, isChecking]);

  useEffect(() => {
    if (!user || !currentBusiness?.id || isChecking) return;
    billingApi.getStatus(currentBusiness.id).then((response) => {
      if (response.data) {
        setBillingStatus(response.data);
        // Gate: a user who owns this business but has not completed web
        // subscription payment must go back to /onboarding. Skip for Stripe
        // Connect return pages so that flow isn't bounced away.
        const onStripeReturnPage = location.pathname.includes('/stripe/');
        const paymentIncomplete = ['incomplete', 'incomplete_expired', 'unpaid'].includes(
          response.data.subscription_status || ''
        );
        if (
          !onStripeReturnPage &&
          !response.data.is_legacy &&
          (!response.data.has_stripe_subscription || paymentIncomplete)
        ) {
          navigate('/onboarding', { replace: true });
        }
      }
    });
  }, [user, currentBusiness?.id, isChecking, location.pathname, navigate]);

  // Fetch user's membership role for the current business
  useEffect(() => {
    if (!user || !currentBusiness?.id || isChecking) return;
    membershipApi.list(currentBusiness.id).then((response) => {
      if (response.data) {
        const membership = response.data.find((m) => m.user === user.id);
        setUserRole(membership?.role || null);
      }
    });
  }, [user, currentBusiness?.id, isChecking]);

  // Show trial expired modal once per session when trial has expired
  useEffect(() => {
    if (!billingStatus || trialExpiredShown.current) return;

    const isMobileSubscriber = billingStatus.subscription_source === 'app_store' ||
      billingStatus.subscription_source === 'play_store';
    const isTrialExpired = !billingStatus.has_stripe_subscription &&
      !billingStatus.is_trialing &&
      !billingStatus.is_active &&
      !isMobileSubscriber;

    if (isTrialExpired) {
      // Check if we've already shown it this session
      const sessionKey = `trial_expired_shown_${currentBusiness?.id}`;
      if (!sessionStorage.getItem(sessionKey)) {
        showTrialExpiredModal();
        sessionStorage.setItem(sessionKey, 'true');
        trialExpiredShown.current = true;
      }
    }
  }, [billingStatus, currentBusiness?.id, showTrialExpiredModal]);

  // Show payment failed modal once per session when subscription is past_due
  useEffect(() => {
    if (!billingStatus || paymentFailedShown.current) return;

    if (billingStatus.subscription_status === 'past_due') {
      const sessionKey = `payment_failed_shown_${currentBusiness?.id}`;
      if (!sessionStorage.getItem(sessionKey)) {
        showPaymentFailedModal();
        sessionStorage.setItem(sessionKey, 'true');
        paymentFailedShown.current = true;
      }
    }
  }, [billingStatus, currentBusiness?.id, showPaymentFailedModal]);

  // Show subscription cancelled modal once per session when returning after cancellation
  useEffect(() => {
    if (!billingStatus || subscriptionCancelledShown.current) return;

    const isCancelled = billingStatus.subscription_status === 'canceled' &&
      billingStatus.has_stripe_subscription;

    if (isCancelled) {
      const sessionKey = `subscription_cancelled_shown_${currentBusiness?.id}`;
      if (!sessionStorage.getItem(sessionKey)) {
        showSubscriptionCancelledModal();
        sessionStorage.setItem(sessionKey, 'true');
        subscriptionCancelledShown.current = true;
      }
    }
  }, [billingStatus, currentBusiness?.id, showSubscriptionCancelledModal]);

  // Show legacy expired modal for expired mobile subscribers
  useEffect(() => {
    if (!billingStatus || legacyExpiredShown.current) return;

    const isLegacyMobileExpired = billingStatus.is_legacy
      && !billingStatus.migrated_at
      && !billingStatus.is_active
      && !billingStatus.has_stripe_subscription
      && (billingStatus.subscription_source === 'play_store' || billingStatus.subscription_source === 'app_store');

    if (isLegacyMobileExpired) {
      const sessionKey = `legacy_expired_shown_${currentBusiness?.id}`;
      if (!sessionStorage.getItem(sessionKey)) {
        showLegacyExpiredModal();
        sessionStorage.setItem(sessionKey, 'true');
        legacyExpiredShown.current = true;
      }
    }
  }, [billingStatus, currentBusiness?.id, showLegacyExpiredModal]);

  const refreshFeatureFlags = () => {
    if (!currentBusiness?.id) return;
    businessApi.getFeatureFlags(currentBusiness.id).then((response) => {
      if (response.data) {
        setFeatureFlags(response.data);
      }
    });
  };

  const refreshUser = async () => {
    const response = await authApi.getMe();
    if (response.data) {
      setUser(response.data);
    }
  };

  const handleBusinessChange = (business: Business) => {
    const currentPath = location.pathname.replace(`/${businessId}`, '');
    navigate(`/${business.id}${currentPath || '/dashboard'}`);
  };

  if (isChecking) {
    return null;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (user.businesses.length === 0) {
    return <Navigate to="/onboarding" replace />;
  }

  if (!currentBusiness && user.businesses.length > 0) {
    return <Navigate to={`/${user.businesses[0].id}/dashboard`} replace />;
  }

  const noSidebarRoutes = ['template-designer', 'contract-editor', 'add-business'];
  const pathWithinBusiness = location.pathname.replace(`/${businessId}/`, '');
  const hideSidebar = noSidebarRoutes.includes(pathWithinBusiness);

  if (hideSidebar) {
    return (
      <AppContext.Provider value={{ user, currentBusiness, featureFlags, billingStatus, userRole, isViewOnly: userRole === 'view_only', refreshFeatureFlags, refreshUser }}>
        <Outlet />
        <UpgradeModal />
        {isTrialExpiredOpen && currentBusiness?.id && (
          <TrialExpiredModal
            businessId={currentBusiness.id}
            onClose={closeTrialExpiredModal}
          />
        )}
        {isPaymentFailedOpen && currentBusiness?.id && (
          <PaymentFailedModal
            businessId={currentBusiness.id}
            onClose={closePaymentFailedModal}
          />
        )}
        {isSubscriptionCancelledOpen && currentBusiness?.id && (
          <SubscriptionCancelledModal
            businessId={currentBusiness.id}
            onClose={closeSubscriptionCancelledModal}
          />
        )}
        {isLegacyExpiredOpen && currentBusiness?.id && (
          <LegacyExpiredModal
            businessId={currentBusiness.id}
            onClose={closeLegacyExpiredModal}
          />
        )}
        <GlobalEmailVerificationModal />
        <GlobalViewOnlyBlockModal />
        {showEmailVerifiedModal && <EmailVerifiedSuccessModal onClose={() => setShowEmailVerifiedModal(false)} />}
      </AppContext.Provider>
    );
  }

  return (
    <AppContext.Provider value={{ user, currentBusiness, featureFlags, billingStatus, userRole, isViewOnly: userRole === 'view_only', refreshFeatureFlags, refreshUser }}>
      <SidebarProvider>
        <AppSidebar
          businesses={user.businesses}
          currentBusiness={currentBusiness}
          onBusinessChange={handleBusinessChange}
          user={user}
        />
        <SidebarInset className="min-w-0">
          <header className="flex h-14 items-center gap-4 border-b bg-background px-6 shrink-0">
            <SidebarTrigger />
            <div className="flex-1" />
            <HeaderAlerts billingStatus={billingStatus} featureFlags={featureFlags} currentBusiness={currentBusiness} user={user} onRefreshUser={refreshUser} onTrialExpiredClick={showTrialExpiredModal} />
            <NotificationBell />
          </header>
          <main className="flex-1 overflow-y-auto overflow-x-hidden bg-gray-50">
            <Outlet />
          </main>
        </SidebarInset>
      </SidebarProvider>
      <UpgradeModal />
      {isTrialExpiredOpen && currentBusiness?.id && (
        <TrialExpiredModal
          businessId={currentBusiness.id}
          onClose={closeTrialExpiredModal}
        />
      )}
      {isPaymentFailedOpen && currentBusiness?.id && (
        <PaymentFailedModal
          businessId={currentBusiness.id}
          onClose={closePaymentFailedModal}
        />
      )}
      {isSubscriptionCancelledOpen && currentBusiness?.id && (
        <SubscriptionCancelledModal
          businessId={currentBusiness.id}
          onClose={closeSubscriptionCancelledModal}
        />
      )}
      {isLegacyExpiredOpen && currentBusiness?.id && (
        <LegacyExpiredModal
          businessId={currentBusiness.id}
          onClose={closeLegacyExpiredModal}
        />
      )}
      <GlobalEmailVerificationModal />
      <GlobalViewOnlyBlockModal />
      {showEmailVerifiedModal && <EmailVerifiedSuccessModal onClose={() => setShowEmailVerifiedModal(false)} />}
    </AppContext.Provider>
  );
}
